import type { DocumentKind, DocumentListParams, DocumentRelation, TagsMode } from '@/api/types'
import { flag, matching, oneOf, readSearch, text } from '@/lib/search-params'
import { parseTags, serializeTags } from '@/lib/tags'

const KINDS = ['file', 'markdown', 'screenshot'] as const
const RELATIONS = ['analysis', 'annex'] as const

/** Valeur d'URL du mode, en français comme le reste des paramètres visibles. */
const MODES = ['toutes', 'au-moins-une'] as const
export type DocumentsTagsMode = (typeof MODES)[number]

/**
 * Filtres de l'onglet Documents, portés par l'URL : la vue est partageable et
 * survit à un rechargement. Les noms sont préfixés `doc` parce qu'ils
 * cohabitent avec ceux des autres onglets de la même fiche client.
 */
export const documentsShape = {
  docQ: text(),
  docType: oneOf(KINDS),
  /** Étiquettes séparées par des virgules, dix au maximum (contrat). */
  docEtiquettes: text(),
  /**
   * Optionnel plutôt que défaut obligatoire : tous les liens vers la fiche
   * client devraient sinon le porter, y compris ceux qui ne vont pas dans
   * l'onglet Documents.
   */
  docMode: oneOf(MODES),
  /**
   * Corbeille : les documents retirés, et eux seuls. Dans l'URL parce qu'on
   * s'envoie « regarde dans la corbeille » comme on s'envoie un filtre.
   */
  docCorbeille: flag(),
  /**
   * Onglet du client (1.33.0) : un projet par sa référence `CLIENT:CODE`, ou
   * `none` pour les documents sans projet. Sans objet dans l'onglet d'un projet.
   */
  docProjet: text(),
  /**
   * Liste à plat (#216) : par défaut, l'onglet du client groupe par projet ;
   * le choix inverse se mémorise dans l'URL, comme un filtre.
   */
  docPlat: flag(),
  /**
   * Lot 17 : les analyses ou les annexes seulement. Le serveur ne rend alors
   * que des attachés ; la liste les montre à plat, chacun avec son parent nommé.
   */
  docRelation: oneOf(RELATIONS),
  /**
   * Lot 20 : le dossier ouvert, absent à la racine. C'est un état de
   * navigation, pas un filtre : « Effacer les filtres » ne le touche pas, et
   * c'est le `web_path` d'une référence `folder` (1.46.1).
   */
  docDossier: matching(/^[0-9a-f]{24}$/),
}

export function parseDocumentsSearch(search: Record<string, unknown>): DocumentsSearch {
  return readSearch(documentsShape, search)
}

export interface DocumentsSearch {
  docQ?: string | undefined
  docType?: DocumentKind | undefined
  docEtiquettes?: string | undefined
  docMode?: DocumentsTagsMode | undefined
  docCorbeille?: boolean | undefined
  docProjet?: string | undefined
  docPlat?: boolean | undefined
  docRelation?: DocumentRelation | undefined
  docDossier?: string | undefined
}

/** Mode retenu quand l'URL n'en porte pas : toutes les étiquettes requises. */
export const DEFAULT_TAGS_MODE: DocumentsTagsMode = 'toutes'

export function toTagsMode(mode: DocumentsTagsMode | undefined): TagsMode {
  return mode === 'au-moins-une' ? 'any' : 'all'
}

/** Traduit les filtres d'URL en query pour `GET /clients/{id}/documents`. */
export function toDocumentListParams(
  search: DocumentsSearch,
  pageSize: number,
): DocumentListParams {
  const tags = parseTags(search.docEtiquettes)
  // Une recherche blanche ne doit pas partir : le serveur la traiterait comme
  // un filtre et ne renverrait rien.
  const q = search.docQ?.trim()

  return {
    q: q === '' ? undefined : q,
    kind: search.docType,
    tags: serializeTags(tags),
    // Le mode n'a de sens qu'avec au moins deux étiquettes ; l'envoyer seul
    // encombrerait l'URL de la requête sans rien changer.
    tags_mode: tags.length > 1 ? toTagsMode(search.docMode) : undefined,
    // `deleted` remplace la liste, il ne s'y ajoute pas : le serveur ne rend
    // alors *que* les retirés, déjà triés par date de retrait.
    deleted: search.docCorbeille ? true : undefined,
    project_id: search.docProjet,
    relation: search.docRelation,
    page_size: pageSize,
  }
}

export function hasActiveDocumentFilters(search: DocumentsSearch): boolean {
  return !!(
    search.docQ ??
    search.docType ??
    search.docEtiquettes ??
    search.docProjet ??
    search.docRelation
  )
}

export const EMPTY_DOCUMENT_FILTERS = {
  docQ: undefined,
  docType: undefined,
  docEtiquettes: undefined,
  docMode: undefined,
  docProjet: undefined,
  docRelation: undefined,
} satisfies DocumentsSearch
